import { useAction, useAtom } from '@reatom/npm-react'
import styled, { css } from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { useTranslation } from '@root/shared/localization'
import { InfoMessage } from '@root/shared/ui-kit'

import {
  UserTradingAnalyticsPhishingPoint,
  type UserTradingAnalyticsPhishing,
} from '../../models/user-trading-analytics-phishing'
import { StatsBlock } from '../common/stats-block'

import { PhishingCheckBlockSkeleton } from './fishing-checks-block-skeleton'
import { PhishingCheckMetric } from './fishing-checks-metric'

import type { PhishingCheckMetricProps } from './fishing-checks-metric'
import type { UserTradingAnalyticsModel } from '../../atoms'

type PhishingCheckBlockProps = {
  model: UserTradingAnalyticsModel
}

export const PhishingCheckBlock = ({ model }: PhishingCheckBlockProps) => {
  const { t } = useTranslation()

  const [checksState] = useAtom(model.requestStateAtom)

  const onRetry = useAction(model.requestStateAtom.refresh)

  return (
    <StatsBlock
      header={
        <StatsBlock.Header>
          <StatsBlock.Title>
            {t('profile.user-trading-analytics.block-title')}
          </StatsBlock.Title>
        </StatsBlock.Header>
      }
    >
      {AsyncStateN.matchWaiting(checksState, {
        Success: (data) => (
          <Main>
            {MAP_PHISHING_TO_METRICS(data.phishing).map((metric, index) => (
              <PhishingCheckMetric key={index} {...metric} />
            ))}
          </Main>
        ),
        Error: () => (
          <ErrorMain>
            <InfoMessage
              intent="error"
              variant="dt"
              onRetry={onRetry}
              css={css`
                width: 100%;
              `}
            />
          </ErrorMain>
        ),
        Waiting: () => (
          <Main>
            {[0, 1, 2, 3].map((key) => (
              <PhishingCheckBlockSkeleton key={key} />
            ))}
          </Main>
        ),
      })}
    </StatsBlock>
  )
}

const MAP_PHISHING_TO_METRICS = (
  phishing: UserTradingAnalyticsPhishing,
): ReadonlyArray<PhishingCheckMetricProps> => [
  {
    type: phishing[UserTradingAnalyticsPhishingPoint.Blacklist].severity,
    titleKey: 'profile.user-trading-analytics.blacklist',
    count: phishing[UserTradingAnalyticsPhishingPoint.Blacklist].count,
    percent: phishing[UserTradingAnalyticsPhishingPoint.Blacklist].percent,
  },
  {
    type: phishing[UserTradingAnalyticsPhishingPoint.DidntBuy].severity,
    titleKey: 'profile.user-trading-analytics.didnt-buy',
    count: phishing[UserTradingAnalyticsPhishingPoint.DidntBuy].count,
    percent: phishing[UserTradingAnalyticsPhishingPoint.DidntBuy].percent,
  },
  {
    type: phishing[UserTradingAnalyticsPhishingPoint.FastFlip].severity,
    titleKey: 'profile.user-trading-analytics.fast-flip',
    count: phishing[UserTradingAnalyticsPhishingPoint.FastFlip].count,
    percent: phishing[UserTradingAnalyticsPhishingPoint.FastFlip].percent,
  },
  {
    type: phishing[UserTradingAnalyticsPhishingPoint.SoldMoreThanBought]
      .severity,
    titleKey: 'profile.user-trading-analytics.sold-more-than-bought',
    count: phishing[UserTradingAnalyticsPhishingPoint.SoldMoreThanBought].count,
    percent:
      phishing[UserTradingAnalyticsPhishingPoint.SoldMoreThanBought].percent,
  },
]

const Main = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 9px;
`

const ErrorMain = styled.div`
  display: flex;
  min-height: 120px;
  align-items: stretch;
`
